import { NoSsr } from "@mui/base";
import { useAtomValue } from "jotai";
import { useRouter } from "next/router";
import { type FunctionComponent } from "react";

import { BaseLink } from "~/components/common/base-link";
import type { HeaderConfiguration } from "~/components/header/types";
import { NavigationBar } from "~/components/plus/navigation-bar";
import { PlusPaywallSuspense } from "~/components/plus/plus-paywall-suspense";
import { sendGA4Tracking } from "~/components/tracking/google-analytics-4/apis";
import type { Destination } from "~/components/tracking/google-analytics-4/types";
import { config } from "~/data";
import { rosettaAtom } from "~/lib/rosetta";
import { pathname as homepagePathname } from "~/pages/plus";
import { pathname as newsAgendaPathname } from "~/pages/plus/agenda";
import { pathname as archivePathname } from "~/pages/plus/archive";
import { SkipPlusLearnMorePlaceholderLocalStorageKey } from "~/pages/plus/learnmore/suspense/consts";
import { pathname as searchPathName } from "~/pages/plus/search";

import { useGreetingMessage } from "./hooks";
import IconAgenda from "./icon-agenda.svg";
import IconArchive from "./icon-archive.svg";
import IconHome from "./icon-home.svg";
import IconPlusLogo from "./icon-plus-logo.svg";
import IconSearch from "./icon-search.svg";
import {
  AvatarContent,
  Container,
  Content,
  DividingLine,
  GreetingMessage,
  HeaderLink,
  IconBackground,
  IconLabel,
  Placeholder,
  PlusHeaderContainer,
  StyledAvatar,
  Wrapper,
} from "./styles";

export type Props = {
  className?: string;
  headerConfiguration?: HeaderConfiguration;
};

type TopLevelPathname =
  | typeof homepagePathname
  | typeof newsAgendaPathname
  | typeof archivePathname
  | typeof searchPathName;

type ActiveStatus = "true" | "";

export const PlusHeader: FunctionComponent<Props> = ({ className, headerConfiguration }) => {
  const { pathname } = useRouter();
  const { greetingMessage } = useGreetingMessage();

  const isActive = (linkPathname: TopLevelPathname): ActiveStatus => {
    if (pathname === linkPathname && homepagePathname === linkPathname) return "true";
    if (pathname.startsWith(linkPathname) && homepagePathname !== linkPathname) return "true";
    return "";
  };

  const handleClickButtonGA4Tracking = () => {
    // GA4 21.1.1
    sendGA4Tracking({
      action: "click",
      category: "search",
      subcategory: "icon",
    });
  };

  const getRedirectClickButtonHandler = (destination: Destination) => () => {
    // GA4 18.1.1
    sendGA4Tracking({
      action: "click",
      category: "menu",
      customized_parameters: {
        action_type: "redirect",
        destination,
        menu_type: "oneline",
        product: "plus",
      },
      subcategory: "section",
    });
  };

  const asyncRosettaState = useAtomValue(rosettaAtom);
  const skipPlusLearnMorePlaceholder =
    typeof window === "undefined"
      ? null
      : window.localStorage.getItem(SkipPlusLearnMorePlaceholderLocalStorageKey);
  const hasScmpPlusAccessRight = asyncRosettaState?.result?.hasScmpPlusAccessRight;
  const isShowPlusHeader =
    headerConfiguration?.appBarVariant !== "scmp/plus/learnmore" &&
    (hasScmpPlusAccessRight || skipPlusLearnMorePlaceholder);

  const queryModule = "oneline_menu";

  return (
    <NoSsr>
      <Container className={className}>
        <NavigationBar />
        {isShowPlusHeader && (
          <PlusHeaderContainer>
            <PlusPaywallSuspense fallback={<Placeholder />}>
              <Wrapper>
                <Content>
                  <HeaderLink
                    $active={isActive(searchPathName)}
                    pathname={searchPathName}
                    onClick={handleClickButtonGA4Tracking}
                  >
                    <IconBackground>
                      <IconSearch />
                    </IconBackground>
                  </HeaderLink>

                  <DividingLine />

                  <HeaderLink
                    $active={isActive(homepagePathname)}
                    pathname={homepagePathname}
                    query={{
                      module: queryModule,
                      pgtype: "homepage",
                    }}
                    onClick={getRedirectClickButtonHandler("homepage")}
                  >
                    <IconBackground>
                      <IconHome />
                    </IconBackground>
                    <IconLabel>Home</IconLabel>
                  </HeaderLink>

                  <HeaderLink
                    $active={isActive(newsAgendaPathname)}
                    pathname={newsAgendaPathname}
                    query={{
                      module: queryModule,
                      pgtype: "agenda",
                    }}
                    onClick={getRedirectClickButtonHandler("agenda")}
                  >
                    <IconBackground>
                      <IconAgenda />
                    </IconBackground>
                    <IconLabel>News Agenda</IconLabel>
                  </HeaderLink>

                  <HeaderLink
                    $active={isActive(archivePathname)}
                    pathname={archivePathname}
                    query={{
                      module: queryModule,
                      pgtype: "archive",
                    }}
                    onClick={getRedirectClickButtonHandler("archive")}
                  >
                    <IconBackground>
                      <IconArchive />
                    </IconBackground>
                    <IconLabel>Plus Archive</IconLabel>
                  </HeaderLink>
                </Content>

                <Content>
                  <IconPlusLogo />
                </Content>

                <AvatarContent>
                  <GreetingMessage>{greetingMessage}</GreetingMessage>
                  <BaseLink pathname={`${config.account.scmpAccountHost}/settings`}>
                    <StyledAvatar id="plus-header" />
                  </BaseLink>
                </AvatarContent>
              </Wrapper>
            </PlusPaywallSuspense>
          </PlusHeaderContainer>
        )}
      </Container>
    </NoSsr>
  );
};

PlusHeader.displayName = "PlusHeader";
